import React from 'react';
import { Card } from './ui/card';
import { ActivityIcon } from '@phosphor-icons/react';

const BMIDisplay = ({ bmiData }) => {
  if (!bmiData) return null;

  // Helper function to get the most recent value (after if available, otherwise before)
  const getValue = (field) => {
    const afterVal = bmiData.after?.[field];
    const beforeVal = bmiData.before?.[field];
    
    // Prioritize 'after' value (most recent treatment result)
    if (afterVal !== undefined && afterVal !== null && afterVal !== '') {
      return afterVal;
    }
    if (beforeVal !== undefined && beforeVal !== null && beforeVal !== '') {
      return beforeVal;
    }
    return '--';
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <ActivityIcon size={24} weight="bold" className="text-red-500" />
        <h3 className="text-lg font-bold text-red-500">BMI Body Analysis Report</h3>
      </div>

      {/* Basic Information */}
      <Card className="p-4 bg-blue-50">
        <h4 className="font-semibold text-[#2C4C3B] mb-3">Basic Information</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          {bmiData.age && (
            <div>
              <span className="text-[#1C1C1A]/60">Age:</span>
              <span className="ml-2 font-medium">{bmiData.age} years</span>
            </div>
          )}
          {bmiData.height && (
            <div>
              <span className="text-[#1C1C1A]/60">Height:</span>
              <span className="ml-2 font-medium">{bmiData.height} cm</span>
            </div>
          )}
          {bmiData.weight && (
            <div>
              <span className="text-[#1C1C1A]/60">Weight:</span>
              <span className="ml-2 font-medium">{bmiData.weight} kg</span>
            </div>
          )}
          {bmiData.body_age && bmiData.body_age !== "-1" && parseInt(bmiData.body_age) > 0 && (
            <div>
              <span className="text-[#1C1C1A]/60">Body Age:</span>
              <span className="ml-2 font-medium">{bmiData.body_age} years</span>
            </div>
          )}
          {bmiData.bp_low && bmiData.bp_high && (
            <div>
              <span className="text-[#1C1C1A]/60">B.P.:</span>
              <span className="ml-2 font-medium">{bmiData.bp_low}/{bmiData.bp_high}</span>
            </div>
          )}
          {bmiData.sugar_low && bmiData.sugar_high && (
            <div>
              <span className="text-[#1C1C1A]/60">Sugar:</span>
              <span className="ml-2 font-medium">{bmiData.sugar_low}/{bmiData.sugar_high}</span>
            </div>
          )}
          {bmiData.health_status && (
            <div>
              <span className="text-[#1C1C1A]/60">Status:</span>
              <span className="ml-2 font-medium capitalize">{bmiData.health_status.replace('_', ' ')}</span>
            </div>
          )}
        </div>
      </Card>

      {/* Body Composition Analysis Table */}
      {(bmiData.before || bmiData.after) && (
        <Card className="p-4">
          <h4 className="font-semibold text-[#2C4C3B] mb-3">Body Composition Analysis</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-gray-300">
                  <th className="border border-gray-400 p-2 text-left font-semibold">Metric</th>
                  <th className="border border-gray-400 p-2 text-center font-semibold">Low</th>
                  <th className="border border-gray-400 p-2 text-center font-semibold">Normal</th>
                  <th className="border border-gray-400 p-2 text-center font-semibold">+High</th>
                  <th className="border border-gray-400 p-2 text-center font-semibold">++Very High</th>
                  <th className="border border-gray-400 p-2 text-center font-semibold bg-gray-400">Yours</th>
                </tr>
              </thead>
              <tbody>
                {/* Body Fat % MEN */}
                <tr>
                  <td className="border border-gray-400 p-2 font-medium">Body Fat % MEN</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">5 - 10</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">10 - 20</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">20 - 25</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">25 - 30+</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold bg-blue-50">
                    {getValue('body_fat_men') !== '--' ? getValue('body_fat_men') : getValue('body_fat')}
                  </td>
                </tr>

                {/* Body Fat % WOMEN */}
                <tr>
                  <td className="border border-gray-400 p-2 font-medium">Body Fat % WOMEN</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">5 - 20</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">20 - 30</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">30 - 35</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">35 - 60+</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold bg-blue-50">
                    {getValue('body_fat_women') !== '--' ? getValue('body_fat_women') : getValue('body_fat')}
                  </td>
                </tr>

                {/* Visceral Fat % */}
                <tr>
                  <td className="border border-gray-400 p-2 font-medium">Visceral Fat %</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">5 - 7</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">10 - 13</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">14 - 15+</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold bg-blue-50">
                    {getValue('visceral_fat')}
                  </td>
                </tr>

                {/* Resting Metabolism */}
                <tr>
                  <td className="border border-gray-400 p-2 font-medium">Resting Metabolism</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">1600</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">1400</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">1800</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">&gt;1800</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold bg-blue-50">
                    {getValue('resting_metabolism')} {getValue('resting_metabolism') !== '--' && 'kcal'}
                  </td>
                </tr>

                {/* BMI (Obesity) */}
                <tr>
                  <td className="border border-gray-400 p-2 font-medium">BMI (Obesity)</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">18.5 - 23</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">23 - 25</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">25 - &gt;30</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold bg-blue-50">
                    {getValue('bmi')} {getValue('bmi') !== '--' && '(BMI)'}
                  </td>
                </tr>

                {/* Body Composition Section Header */}
                <tr className="bg-gray-200">
                  <td className="border border-gray-400 p-2 font-bold">Body Composition</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold text-xs">Diagnose</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold text-xs">OK</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold text-xs">Good</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold text-xs">Very Good</td>
                  <td className="border border-gray-400 p-2 text-center font-bold bg-gray-300">Yours</td>
                </tr>

                {/* Whole Body Fat */}
                <tr>
                  <td className="border border-gray-400 p-2 font-medium">Whole Body Fat</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">20-30</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold bg-blue-50">
                    {getValue('whole_body_fat')} {getValue('whole_body_fat') !== '--' && 'kg'}
                  </td>
                </tr>

                {/* Trunk Fat % */}
                <tr>
                  <td className="border border-gray-400 p-2 font-medium">Trunk Fat %</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">Men</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">&lt; 15%</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">Women</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">&lt; 30%</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold bg-blue-50">
                    {getValue('trunk_fat')} {getValue('trunk_fat') !== '--' && '%'}
                  </td>
                </tr>

                {/* Arms Fat */}
                <tr>
                  <td className="border border-gray-400 p-2 font-medium">Arms Fat</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">15.4%</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold bg-blue-50">
                    {getValue('arms_fat')} {getValue('arms_fat') !== '--' && '%'}
                  </td>
                </tr>

                {/* Leg Fat */}
                <tr>
                  <td className="border border-gray-400 p-2 font-medium">Leg Fat</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">24.8%</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold bg-blue-50">
                    {getValue('leg_fat')} {getValue('leg_fat') !== '--' && '%'}
                  </td>
                </tr>

                {/* Skeletal Muscle MEN */}
                <tr>
                  <td className="border border-gray-400 p-2 font-medium">Skeletal Muscle MEN</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">1 - 33</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">33 - 36</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">36 - 38</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">&gt; 38</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold bg-blue-50">
                    {(() => {
                      const val = getValue('skeletal_muscle_men') !== '--' ? getValue('skeletal_muscle_men') : getValue('skeletal_muscle');
                      return val !== '--' ? `${val} kg` : '--';
                    })()}
                  </td>
                </tr>

                {/* Skeletal Muscle WOMEN */}
                <tr>
                  <td className="border border-gray-400 p-2 font-medium">Skeletal Muscle WOMEN</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">1 - 26</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">26 - 28</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">28 - 30</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">&gt; 30</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold bg-blue-50">
                    {(() => {
                      const val = getValue('skeletal_muscle_women') !== '--' ? getValue('skeletal_muscle_women') : getValue('skeletal_muscle');
                      return val !== '--' ? `${val} kg` : '--';
                    })()}
                  </td>
                </tr>

                {/* Trunk Skeletal Muscle */}
                <tr>
                  <td className="border border-gray-400 p-2 font-medium">Trunk Skeletal Muscle</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">26.9%</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold bg-blue-50">
                    {getValue('trunk_skeletal_muscle')} {getValue('trunk_skeletal_muscle') !== '--' && '%'}
                  </td>
                </tr>

                {/* Arms Skeletal Muscle */}
                <tr>
                  <td className="border border-gray-400 p-2 font-medium">Arms Skeletal Muscle</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">24.2%</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold bg-blue-50">
                    {getValue('arms_skeletal_muscle')} {getValue('arms_skeletal_muscle') !== '--' && '%'}
                  </td>
                </tr>

                {/* Leg Skeletal Muscle */}
                <tr>
                  <td className="border border-gray-400 p-2 font-medium">Leg Skeletal Muscle</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">38.8%</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center text-xs">--</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold bg-blue-50">
                    {getValue('leg_skeletal_muscle')} {getValue('leg_skeletal_muscle') !== '--' && '%'}
                  </td>
                </tr>

                {/* Health Status */}
                <tr>
                  <td className="border border-gray-400 p-2 font-medium">Health / Under Fat / Over Fat</td>
                  <td className="border border-gray-400 p-2 text-center text-xs" colSpan="4">
                    {bmiData.health_status ? (
                      <span className="font-semibold capitalize">{bmiData.health_status.replace('_', ' ')}</span>
                    ) : '--'}
                  </td>
                  <td className="border border-gray-400 p-2 text-center font-semibold bg-blue-50 text-xs">select</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="text-xs text-[#1C1C1A]/60 italic">
        * Reference ranges based on WHO and medical standards
      </div>
    </div>
  );
};

export default BMIDisplay;

